import { NgClass, NgStyle } from '@angular/common';
import { Component, DestroyRef, ElementRef, ViewChild, computed, inject, OnInit, AfterViewInit, output } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { Menu } from './sidebar';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { iconsConfig } from './sidebar.icons';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatTooltip } from '@angular/material/tooltip';
import { ShowIfAuthorizedModule } from 'src/app/directives/show-if-authorized/show-if-authorized.module';
import { IStepOption, TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { TourStepsService } from 'src/app/services/tour-steps.service';
import { ApiUsuarioService } from 'src/app/services/api-usuario.service';
import { GaEventModule } from 'src/app/directives/ga-event/ga-event.module';

@Component({
  selector: 'app-sidebar',
  imports: [
    MatIcon,
    NgClass,
    NgStyle,
    NgIcon,
    RouterLink,
    MatTooltip,
    ShowIfAuthorizedModule,
    TourMatMenuModule,
    GaEventModule
  ],
  providers: [provideIcons(iconsConfig)],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [ // Quando o elemento entra
        style({ opacity: 0 }),
        animate('120ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [ // Quando o elemento sai
        animate('120ms ease-out', style({ opacity: 0 })),
      ]),
    ])
  ]
})
export class SidebarComponent implements AfterViewInit {
  protected router = inject(Router);
  public tourStepsService = inject(TourStepsService);
  private destroyRef = inject(DestroyRef);
  private apiUsuario = inject(ApiUsuarioService);

  public closeTourEvent = output();

  private readonly SIDEBAR_CLOSED_KEY = 'sidebar-is-closed';
  public isClosed = this.getInitialClosedState();
  public isMenuScrollable = false;

  private getInitialClosedState(): boolean {
    try {
      return localStorage.getItem(this.SIDEBAR_CLOSED_KEY) === 'true';
    } catch {
      return false;
    }
  }

  protected isFilial = computed(() => {
    const sessao = this.apiUsuario.sessao();
    return !!sessao?.idCompanyGroup && !sessao?.companyGroupAdmin;
  });
  protected isMatriz = computed(() => {
    const sessao = this.apiUsuario.sessao();
    return !!sessao?.companyGroupAdmin;
  });
  @ViewChild('menu', { static: true }) private menuRef?: ElementRef<HTMLElement>;
  private menuMutationObserver?: MutationObserver;
  private rafId?: number;
  private scrollCheckTimeout?: any;
  
  protected menuList: Menu[] = [
    {
      icon: "heroCalculator",
      txt: "Cotações",
      url: "/cotacoes",
      tourAnchor: 'cotacoes-btn',
      showIfAuthorizedName: true,
      gaEvent: "ID_MENU_COTACOES"
    },
    {
      icon: "heroRectangleGroup",
      txt: "Dashboard",
      url: "/dashboard",
      tourAnchor: 'dashboard-btn',
      showIfAuthorizedName: "DASHBOARD",
      gaEvent: "ID_MENU_DASHBOARD"
    },
    {
      icon: "heroClipboardDocument",
      txt: "Tarefas",
      url: "/tarefas",
      tourAnchor: 'tarefas-btn',
      showIfAuthorizedName: "TAREFAS",
      gaEvent: "ID_MENU_TAREFAS"
    },
    {
      icon: "heroUsers",
      txt: "Clientes",
      url: "/clientes",
      tourAnchor: "clientes-btn",
      showIfAuthorizedName: "CLIENTES",
      gaEvent: "ID_MENU_CLIENTES"
    },
    {
      icon: "heroArrowPath",
      txt: "Sincronizações",
      isOpen: false,
      tourAnchor: "sincronizacoes-btn",
      showIfAuthorizedName: "SINCRONIZACOES",
      gaEvent: "ID_MENU_SINCRONIZACOES",

      // ! IMPORTANTE: Algumas páginas não estão prontas (pendências back-end), então ficaram comentadas. Conforme o back-end seja atualizado, vamos ajustando por aqui!
      subMenu: [
        {
          icon: "heroDocumentTextSolid",
          txt: "Propostas",
          url: "/propostas",
          isActive: false,
          showIfAuthorizedName: "PROPOSTAS",
          gaEvent: "ID_SB_SINC_PROPOSTAS"
        },
        {
          icon: "heroShieldCheckSolid",
          url: "/apolices",
          txt: "Apólices",
          isActive: false,
          showIfAuthorizedName: "APOLICES",
          gaEvent: "ID_SB_SINC_APOLICES"
        },
        {
          icon: "heroDocumentCurrencyDollarSolid",
          url: "/extratos",
          txt: "Extratos",
          isActive: false,
          showIfAuthorizedName: "EXTRATOS",
          gaEvent: "ID_SB_SINC_EXTRATOS"
        },
        {
          icon: "heroRectangleStackSolid",
          url: "/parcelas-pendentes",
          txt: "Parcelas pendentes",
          isActive: false,
          showIfAuthorizedName: "PARCELAS",
          gaEvent: "ID_SB_SINC_PARCPENDENTES"
        },
        // {
        //   icon: "heroDocumentPlusSolid",
        //   url: "/renovacoes-pendentes",
        //   txt: "Renovações",
        //   isActive: false,
        //   showIfAuthorizedName: "RENOVACOES_PENDENTES",
        //   gaEvent: "ID_SB_SINC_RENOVACOES"
        // },
      ],
    },
    {
      icon: "heroListBullet",
      txt: "Negócios",
      isOpen: false,
      tourAnchor: "negocios-btn",
      showIfAuthorizedName: "NEGOCIOS",
      gaEvent: "ID_MENU_NEGOCIOS",
      subMenu: [
        {
          icon: "heroFunnelSolid",
          txt: "Funil de vendas",
          url: "/funil-de-vendas",
          isActive: false,
          showIfAuthorizedName: "NEGOCIOS",
          gaEvent: "ID_SB_NEGOCIOS_FUNIL"
        },
        {
          icon: "heroChartBarSolid",
          txt: "Lista de negócios",
          url: "/lista-negocios",
          isActive: false,
          showIfAuthorizedName: "NEGOCIOS",
          gaEvent: "ID_SB_NEGOCIOS_LISTA"
        },
      ]
    },
    {
      icon: "heroLink",
      txt: "Minhas plataformas",
      isOpen: false,
      tourAnchor: "minhas-plataformas-btn",
      showIfAuthorizedName: 'MINHAS_PLATAFORMAS',
      gaEvent: "ID_MENU_MINHAS_PLATAFORMAS",
      subMenu: [
        {
          icon: "heroComputerDesktopSolid",
          txt: "Online",
          url: "/online",
          isActive: false,
          showIfAuthorizedName: "CORRETOR_DIGITAL",
          gaEvent: "ID_SB_MP_ONLINE"
        },
        {
          icon: "toSeguradoOutline",
          txt: "Tô segurado",
          url: "/to-segurado",
          isActive: false,
          showIfAuthorizedName: "TO_SEGURADO",
          gaEvent: "ID_SB_MP_TO_SEGURADO"
        },
      ]
    },
    {
      icon: "heroCog8Tooth",
      txt: "Configurações",
      isOpen: false,
      tourAnchor: "configuracoes-btn",
      gaEvent: "ID_MENU_CONFIGURACOES",
      subMenu: [        
        {
          icon: "heroUserGroupSolid",
          txt: "Usuários e permissões",
          url: "/config-usuarios",
          isActive: false,
          showIfAuthorizedName: "USUARIOS_PERMISSOES",
          hideIfFilial: true,
          gaEvent: "ID_SB_CONF_USUARIOS"
        },
        {
          icon: "heroBuildingOffice2Solid",
          txt: "Seguradoras",
          url: "/config-seguradoras",
          isActive: false,
          showIfAuthorizedName: "SEGURADORAS",
          gaEvent: "ID_SB_CONF_SEGURADORAS"
        },
        {
          icon: "heroClipboardDocumentListSolid",
          txt: "Coberturas",
          url: "/config-coberturas",
          isActive: false,
          showIfAuthorizedName:"COBERTURAS",
          gaEvent: "ID_SB_CONF_COBERTURAS"
        },
        {
          icon: "heroSquare3Stack3dSolid",
          txt: "Grupos de corretoras",
          url: "/config-grupos",
          isActive: false,
          showIfAuthorizedName: "PERFIL",
          showIfMatriz: true,
      },
      ],
      showIfAuthorizedName: "BOTAO_CONFIGURACOES",
    },
  ];

  ngAfterViewInit(): void {
    this.scheduleUpdateMenuScrollable();
    const onResize = () => this.scheduleUpdateMenuScrollable();
    window.addEventListener('resize', onResize);
    const el = this.menuRef?.nativeElement;
    if (el && 'ResizeObserver' in window) {
      const ro = new ResizeObserver(() => this.scheduleUpdateMenuScrollable());
      ro.observe(el);
      this.destroyRef.onDestroy(() => ro.disconnect());
    }
    if (el && 'MutationObserver' in window) {
      this.menuMutationObserver = new MutationObserver(() => this.scheduleUpdateMenuScrollable());
      this.menuMutationObserver.observe(el, { childList: true, subtree: true, attributes: true });
      this.destroyRef.onDestroy(() => this.menuMutationObserver?.disconnect());
    }
    this.destroyRef.onDestroy(() => window.removeEventListener('resize', onResize));
  }

  public changeOpenSidebar = () => {
    if (!this.isClosed) {
      this.menuList.map(item => item.isOpen = false);
    }

    this.isClosed = !this.isClosed;
    try {
      localStorage.setItem(this.SIDEBAR_CLOSED_KEY, this.isClosed.toString());
    } catch {}
    this.scheduleUpdateMenuScrollable();
  }

  public openSubmenu = (item: Menu) => {
    const hasSubmenuOpened = this.menuList.some(item => item.isOpen);
    
    if (!item.isOpen && hasSubmenuOpened) {
      this.menuList.map(item => item.isOpen = false);      
    }

    if (this.isClosed) {
      this.isClosed = false;
      try {
        localStorage.setItem(this.SIDEBAR_CLOSED_KEY, 'false');
      } catch {}
    }

    setTimeout(() => {
      item.isOpen = !item.isOpen;
      this.scheduleUpdateMenuScrollable();
    }, !this.isClosed ? 200 : 0);
  }

  public closeTour = () => {
    this.closeTourEvent.emit();
  }

  private updateMenuScrollable(): void {
    const el = this.menuRef?.nativeElement;
    if (!el) return;
    this.isMenuScrollable = (el.scrollHeight - el.clientHeight) > 1;
  }

  private scheduleUpdateMenuScrollable(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.scrollCheckTimeout) clearTimeout(this.scrollCheckTimeout);

    this.updateMenuScrollable();
    this.rafId = requestAnimationFrame(() => this.updateMenuScrollable());
    this.scrollCheckTimeout = setTimeout(() => this.updateMenuScrollable(), 220);
  }
}
